import { useRef, useEffect, useState } from "react";

interface FitTextProps {
  children: React.ReactNode;
  maxSize?: number;
  minSize?: number;
  className?: string;
  "data-testid"?: string;
}

/**
 * Renders children text at the largest font size that fits within the container width.
 * Starts at `maxSize` and steps down to `minSize` until no overflow.
 */
export function FitText({
  children,
  maxSize = 30,
  minSize = 12,
  className = "",
  "data-testid": testId,
}: FitTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState(maxSize);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let mounted = true;

    function recalculate() {
      if (!container || !mounted) return;
      let size = maxSize;
      container.style.fontSize = `${size}px`;
      while (size > minSize && container.scrollWidth > container.clientWidth) {
        size -= 1;
        container.style.fontSize = `${size}px`;
      }
      if (mounted) setFontSize(size);
    }

    function scheduleRecalculate() {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(recalculate);
    }

    scheduleRecalculate();

    const observer = new ResizeObserver(scheduleRecalculate);
    observer.observe(container);

    return () => {
      mounted = false;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      observer.disconnect();
    };
  }, [children, maxSize, minSize]);

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden whitespace-nowrap ${className}`}
      style={{ fontSize: `${fontSize}px` }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}
